import type { DeviceSearchFilters } from '@/api/deviceApi'
import type { OrgFrontendSettings } from '@/stores/auth'
import type { InvoiceDownloadResponse, InvoiceHistoryResponse } from '@/types/invoice'
import type { OrgInfoProductApi } from '@/types/organization-owner-api'
import type { ManagePaymentSourcesResponse, RegeneratePaymentLinkResponse, UpgradeResponse } from '@/types/subscription'
import { asList } from '@/utils/asList'
import { isConsoleDeployment } from '@/utils/deployment'
import { apiCall, consoleApiCall, ownerApiCall } from './axiosInstance'

export interface BulkProvisionPayload {
	count: number
	lifetime: number | null
	generate_mtls_creds: boolean
	generate_http_creds: boolean
	metadata?: Record<string, string>
}

export interface CreateOrganizationRequest {
	name: string
	broker_host: string
	broker_port: number
	planId?: string
	period?: 'Monthly' | 'Yearly'
}

export interface OrganizationBrokerSettings {
	broker_host: string
	broker_port: number
}

export interface CreateOrganizationResponse {
	id: string
	status: string
}

export interface GetOwnersResponse {
	results: string[]
}

export interface AddOwnerRequest {
	email: string
}

export interface AddOwnerResponse {
	status: string
}

export interface DeleteOwnerResponse {
	status: string
}

export interface DeleteOrganizationResponse {
	status: string
}

export interface UpdateOrganizationParams {
	name?: string
	broker_host?: string
	broker_port?: number
	/** The endpoint replaces this object, so a caller sends back every key it does not own. */
	frontend_settings?: OrgFrontendSettings
}

export interface UpdateOrganizationResponse {
	id: string
	name: string
	broker_host?: string | null
	broker_port?: number | null
	frontend_settings?: UpdateOrganizationParams['frontend_settings']
}

// Per-request timeout/retry overrides for org actions; omitted → backend applies the renew_cert
// action type defaults of the tenant the action runs in (root project for attach).
export interface ActionTimeoutRetryOverrides {
	timeout_seconds?: number | null // null = explicitly no timeout
	retry_on_timeout_count?: number
}

export interface ReprovisionDeviceRequest extends ActionTimeoutRetryOverrides {
	target_tenant_id: string
	target_device_id: string
	lifetime_seconds?: number // backend defaults to 315360000 (10 years)
	generate_mtls_certs?: boolean // backend default true
	generate_http_certs?: boolean // backend default false
}

export interface ReprovisionDeviceResponse {
	action_id: number // id of the queued renew_cert action
}

export interface AttachDeviceRequest {
	tenant_id: string // target tenant, must belong to org
	device_id: string // 1–63 chars, [A-Za-z0-9_-]
}

export interface AttachDeviceResponse {
	uid: number
	tenant_id: string
	device_id: string
}

export interface AttachDeviceItem {
	uid: number
	tenant_id: string
	device_id: string
}

export interface AttachDevicesRequest extends ActionTimeoutRetryOverrides {
	devices: AttachDeviceItem[]
}

/** The bulk endpoints report a count only; they stopped returning a row per device. */
export interface AttachDevicesResponse {
	count: number
}

export interface ReprovisionDevicesForFilterRequest extends ActionTimeoutRetryOverrides {
	// Omitted entirely, this matches every active device in the org.
	device_filter?: DeviceSearchFilters
	target_tenant_id: string
	lifetime_seconds?: number // backend defaults to 315360000 (10 years)
	generate_mtls_certs?: boolean // backend default true
	generate_http_certs?: boolean // backend default false
}

/** A device the backend left out of the batch because an action was already in flight on it. */
export interface ReprovisionSkippedDevice {
	uid: number
	/** Backend enum, currently `reprovision_in_progress` or `action_pending`. */
	reason: string
}

export interface ReprovisionDevicesForFilterResponse {
	count: number
	/** Absent on backends that still reject the whole batch instead of skipping. */
	skipped?: ReprovisionSkippedDevice[]
}

export interface CertGenerationOptions {
	generate_mtls_certs?: boolean
	generate_http_certs?: boolean
}

export const organizationsApi = {
	createOrganization: async (
		name: string,
		brokerSettings: OrganizationBrokerSettings,
		options?: { planId?: string; period?: 'Monthly' | 'Yearly' },
	): Promise<CreateOrganizationResponse> => {
		const data: CreateOrganizationRequest = { name, ...brokerSettings }
		if (options?.planId && options?.period) {
			data.planId = options.planId
			data.period = options.period
		}
		if (isConsoleDeployment()) {
			return consoleApiCall<CreateOrganizationResponse>('/v1/organizations', {
				method: 'POST',
				data,
			})
		}
		// Platform: use v3 API
		return ownerApiCall<CreateOrganizationResponse>('/v3/organizations', {
			method: 'POST',
			data,
		})
	},
	regeneratePaymentLink: async (organizationId: string): Promise<RegeneratePaymentLinkResponse> => {
		return consoleApiCall<RegeneratePaymentLinkResponse>(
			`/v1/organizations/${encodeURIComponent(organizationId)}/regenerate-payment-link`,
			{
				method: 'POST',
			},
		)
	},
	subscribeOrganization: async (
		organizationId: string,
		planId: string,
		period: 'Yearly' | 'Monthly',
	): Promise<UpgradeResponse> => {
		return consoleApiCall<UpgradeResponse>(`/v1/organizations/${encodeURIComponent(organizationId)}/subscribe`, {
			method: 'POST',
			data: {
				plan_id: planId,
				period,
			},
		})
	},
	modifySubscription: async (
		organizationId: string,
		planId: string,
		period: 'Yearly' | 'Monthly',
	): Promise<UpgradeResponse> => {
		return consoleApiCall<UpgradeResponse>(`/v1/organizations/${encodeURIComponent(organizationId)}/modify`, {
			method: 'POST',
			data: {
				plan_id: planId,
				period,
			},
		})
	},
	getOwners: async (organizationId: string): Promise<GetOwnersResponse> => {
		if (isConsoleDeployment()) {
			return consoleApiCall<GetOwnersResponse>(`/v1/organizations/${encodeURIComponent(organizationId)}/owners`)
		}
		// Platform: use v3 API
		return apiCall<GetOwnersResponse>(`/v3/organizations/${encodeURIComponent(organizationId)}/owners`)
	},
	addOwner: async (organizationId: string, email: string): Promise<AddOwnerResponse> => {
		if (isConsoleDeployment()) {
			return consoleApiCall<AddOwnerResponse>(`/v1/organizations/${encodeURIComponent(organizationId)}/owners`, {
				method: 'POST',
				data: { email },
			})
		}
		// Platform: use v3 API
		return apiCall<AddOwnerResponse>(`/v3/organizations/${encodeURIComponent(organizationId)}/owners`, {
			method: 'POST',
			data: { email },
		})
	},
	deleteOwner: async (organizationId: string, email: string): Promise<DeleteOwnerResponse> => {
		if (isConsoleDeployment()) {
			return consoleApiCall<DeleteOwnerResponse>(`/v1/organizations/${encodeURIComponent(organizationId)}/owners`, {
				method: 'DELETE',
				data: { email },
			})
		}
		// Platform: use v3 API
		return apiCall<DeleteOwnerResponse>(`/v3/organizations/${encodeURIComponent(organizationId)}/owners`, {
			method: 'DELETE',
			data: { email },
		})
	},
	deleteOrganization: async (organizationId: string): Promise<DeleteOrganizationResponse> => {
		if (isConsoleDeployment()) {
			return consoleApiCall<DeleteOrganizationResponse>(`/v1/organizations/${encodeURIComponent(organizationId)}`, {
				method: 'DELETE',
			})
		}
		// Platform: use v3 API
		return apiCall<DeleteOrganizationResponse>(`/v3/organizations/${encodeURIComponent(organizationId)}`, {
			method: 'DELETE',
		})
	},
	updateOrganization: async (
		organizationId: string,
		params: UpdateOrganizationParams,
	): Promise<UpdateOrganizationResponse> => {
		if (isConsoleDeployment()) {
			return consoleApiCall<UpdateOrganizationResponse>(`/v1/organizations/${encodeURIComponent(organizationId)}`, {
				method: 'PATCH',
				data: params,
			})
		}
		return ownerApiCall<UpdateOrganizationResponse>(`/v3/organizations/${encodeURIComponent(organizationId)}`, {
			method: 'PATCH',
			data: params,
		})
	},
	getInvoices: async (organizationId: string): Promise<InvoiceHistoryResponse> => {
		return consoleApiCall<InvoiceHistoryResponse>(`/v1/organizations/${encodeURIComponent(organizationId)}/invoices`)
	},
	downloadInvoice: async (organizationId: string, invoiceId: string): Promise<InvoiceDownloadResponse> => {
		return consoleApiCall<InvoiceDownloadResponse>(
			`/v1/organizations/${encodeURIComponent(organizationId)}/invoices/${encodeURIComponent(invoiceId)}/download`,
		)
	},
	managePaymentSources: async (organizationId: string): Promise<ManagePaymentSourcesResponse> => {
		return consoleApiCall<ManagePaymentSourcesResponse>(
			`/v1/organizations/${encodeURIComponent(organizationId)}/manage_payment_sources`,
			{
				method: 'POST',
			},
		)
	},
	getSubscriptionInfo: async (organizationId: string): Promise<OrgInfoProductApi> => {
		return consoleApiCall(`/v3/organizations/${encodeURIComponent(organizationId)}/subscription`)
	},
	reprovisionDevice: async (
		organizationId: string,
		uid: number,
		params: ReprovisionDeviceRequest,
	): Promise<ReprovisionDeviceResponse> => {
		return apiCall<ReprovisionDeviceResponse>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/devices/${encodeURIComponent(String(uid))}/reprovision`,
			{
				method: 'POST',
				data: params,
			},
		)
	},
	detachDevice: async (
		organizationId: string,
		uid: number,
	): Promise<{ uid: number; tenant_id: null; device_id: null }> => {
		return apiCall<{ uid: number; tenant_id: null; device_id: null }>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/devices/${encodeURIComponent(String(uid))}/detach`,
			{ method: 'POST' },
		)
	},
	/**
	 * The filter form of bulk reprovision. The backend resolves the filter across every project in
	 * the org and derives each target device id from the uid, so it only fits detach.
	 */
	reprovisionDevicesForFilter: async (
		organizationId: string,
		body: ReprovisionDevicesForFilterRequest,
	): Promise<ReprovisionDevicesForFilterResponse> => {
		const response = await apiCall<ReprovisionDevicesForFilterResponse>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/devices/reprovision-filter`,
			{
				method: 'POST',
				data: body,
			},
		)
		// uid is an i64 server-side and can arrive JSON-stringified.
		return {
			count: Number(response?.count ?? 0),
			skipped: asList(response?.skipped).map(device => ({ ...device, uid: Number(device.uid) })),
		}
	},
	// Bulk detach without enumerating the set: an id list of a whole fleet overruns the body limit.
	detachDevicesForFilter: async (
		organizationId: string,
		deviceFilter: DeviceSearchFilters,
		rootTenantId: string,
		options?: CertGenerationOptions,
		timeoutRetryOverrides?: ActionTimeoutRetryOverrides,
	): Promise<ReprovisionDevicesForFilterResponse> => {
		return organizationsApi.reprovisionDevicesForFilter(organizationId, {
			device_filter: deviceFilter,
			target_tenant_id: rootTenantId,
			generate_mtls_certs: options?.generate_mtls_certs ?? false,
			generate_http_certs: options?.generate_http_certs ?? false,
			...timeoutRetryOverrides,
		})
	},
	// Attach is expressed through /reprovision: the root-project device (uid) is
	// reprovisioned into the target project under the given device id.
	attachDeviceViaReprovision: async (
		organizationId: string,
		uid: number,
		targetTenantId: string,
		targetDeviceId: string,
		options?: CertGenerationOptions,
		timeoutRetryOverrides?: ActionTimeoutRetryOverrides,
	): Promise<ReprovisionDeviceResponse> => {
		return organizationsApi.reprovisionDevice(organizationId, uid, {
			target_tenant_id: targetTenantId,
			target_device_id: targetDeviceId,
			generate_mtls_certs: options?.generate_mtls_certs ?? false,
			generate_http_certs: options?.generate_http_certs ?? false,
			...timeoutRetryOverrides,
		})
	},
	attachDevice: async (
		organizationId: string,
		uid: number,
		params: AttachDeviceRequest,
	): Promise<AttachDeviceResponse> => {
		return apiCall<AttachDeviceResponse>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/devices/${encodeURIComponent(String(uid))}/attach`,
			{
				method: 'POST',
				data: params,
			},
		)
	},
	attachDevices: async (organizationId: string, body: AttachDevicesRequest): Promise<AttachDevicesResponse> => {
		return apiCall<AttachDevicesResponse>(`/v3/organizations/${encodeURIComponent(organizationId)}/devices/attach`, {
			method: 'POST',
			data: body,
		})
	},
}
